import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const SCREENSHOT_ALT =
  "Curipod's TEKS-Aligned Practice page with Bluebonnet and 3rd Grade selected, showing Unit 1, Lesson 1 activities with time, type, when-to-use tags, TEKS, what students do, and why this activity.";

const BluebonnetInterventionActivitiesTier2Tier3 = () => {
  const post = getBlogPost('bluebonnet-intervention-activities-tier-2-tier-3');
  return (
    <BlogArticle post={post}>
      <p>
        {
          "If you are looking for Bluebonnet intervention and small group activities that match the lesson your teachers taught this morning, you can now find them by unit, by lesson and by TEKS in one place. This guide shows you how a campus can use Curipod's TEKS-aligned practice for Bluebonnet Learning in grades 3–5, including short constructed response (SCR), extended constructed response (ECR), revising and editing, and writing practice, during Tier 2 and Tier 3 time."
        }
      </p>
      <p>
        {
          "Your Tier 1 block already belongs to Bluebonnet. Teachers follow the read-aloud, the reader and the daily lesson as written. The question most literacy leaders ask is what happens in the next 30 minutes: WIN time, small group rotations, intervention, and the Pausing Point days built into each unit. TEA's own guidance points to those same spaces. Its field guide for Bluebonnet K–5 RLA names small groups, partners and Pausing Points as places to give students extra support, reteaching and modified pacing."
        }
      </p>
      <p>{'The practice library is built for that time. Here is how to use it.'}</p>
      <h2>{'How the library is organized'}</h2>
      <figure>
        <img src={post.cover} alt={SCREENSHOT_ALT} />
        <figcaption>
          {
            'The TEKS-Aligned Practice page with Bluebonnet and 3rd Grade selected. The library covers grades 3–5. Units 1 to 6 are open in this view, and units 7 to 10 show a lock.'
          }
        </figcaption>
      </figure>
      <p>
        {
          'At the top of the page you pick the curriculum and the grade: 3rd, 4th or 5th. The page then shows one tab per Bluebonnet unit. Inside a unit, a row of lesson numbers lets you jump straight to the lesson your class is on, and a Filter menu narrows the list.'
        }
      </p>
      <p>
        {
          'Each lesson has its own set of Curipod activities. Every row tells a teacher seven things:'
        }
      </p>
      <table>
        <thead>
          <tr>
            <th>{'Column'}</th>
            <th>{'What it tells you'}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{'Curipod lesson'}</td>
            <td>{'The activity name, linked so a teacher can open it'}</td>
          </tr>
          <tr>
            <td>{'Time'}</td>
            <td>{'How long it runs, for example about 5 minutes'}</td>
          </tr>
          <tr>
            <td>{'Type'}</td>
            <td>
              {
                'The kind of practice: SCR, ECR, Revising & Editing, Writing or Vocabulary'
              }
            </td>
          </tr>
          <tr>
            <td>{'When to use'}</td>
            <td>
              {'Tags such as Small Group, Intervention, Review, Warm-Up and Word Work'}
            </td>
          </tr>
          <tr>
            <td>{'TEKS'}</td>
            <td>{'The standard the activity practices'}</td>
          </tr>
          <tr>
            <td>{'What students do'}</td>
            <td>{'The task in one sentence'}</td>
          </tr>
          <tr>
            <td>{'Why this activity'}</td>
            <td>{"How it connects to that day's Bluebonnet lesson"}</td>
          </tr>
        </tbody>
      </table>
      <p>
        {
          'Take Grade 3, Unit 1, Lesson 1, "The River Bank, Part I." The first row is a vocabulary activity for TEKS 3.7.F. Students write a sentence using the word "meandered" and then vote on the best one. The second is a Quick Write for TEKS 3.8.B. Students write a character\'s inner thoughts to show that character\'s traits. Both take about five minutes, and both carry a Small Group tag.'
        }
      </p>
      <p>
        <Link href="/signup" passHref>
          <a>
            {
              'Sign up free to browse the full Bluebonnet practice library for grades 3–5.'
            }
          </a>
        </Link>
      </p>
      <h2>{'Three ways to find the right activity'}</h2>
      <p>
        {
          'Teachers come to the page with different starting points. The library works for each of them.'
        }
      </p>
      <p>
        <strong>{'Start from the lesson.'}</strong>
        {
          " A teacher who wants to reteach today's skill picks the unit tab, taps the lesson number and reads down the rows. Because every activity is tied to a specific lesson, the texts, vocabulary and skills already feel familiar to students."
        }
      </p>
      <p>
        <strong>{'Start from the TEKS.'}</strong>
        {
          ' An interventionist who knows a group needs work on character relationships, or on using new vocabulary, scans the TEKS column for that standard. The same TEKS shows up across several lessons in a unit, so a group can practice one standard on several days with fresh content each time.'
        }
      </p>
      <p>
        <strong>{'Start from the type.'}</strong>
        {
          ' The library has five activity types: SCR, ECR, Revising & Editing, Writing and Vocabulary. Some days a teacher wants a quick written answer. Other days the group needs a longer response, or a round of revising and editing sentences. The Type column and the Filter menu let the teacher pick the format first, and looking across the lessons in a unit gives the teacher more choices for the same format.'
        }
      </p>
      <h2>{'What a five-minute small group looks like'}</h2>
      <p>
        {
          'Picture a teacher at the kidney table with six students during WIN time. The group read "The River Bank" in Tier 1 that morning. The teacher opens the Quick Write from the library.'
        }
      </p>
      <ol>
        <li>
          {
            'The teacher reads the prompt aloud and reminds students of the character they met in the read-aloud.'
          }
        </li>
        <li>
          {"Each student writes the character's inner thoughts on their own device."}
        </li>
        <li>
          {
            'Curipod gives every student feedback on their writing within seconds. The teacher sees all six responses at once.'
          }
        </li>
        <li>
          {
            'The teacher picks two responses to talk through with the group: what the thoughts show about the character, and which words make that clear.'
          }
        </li>
        <li>
          {
            'Students make one change to their writing based on the feedback and the discussion.'
          }
        </li>
      </ol>
      <p>
        {
          "That is one attempt, a short conversation and a second attempt in about five minutes. The teacher stays in charge of every step, decides which responses to discuss, and can move straight to the next lesson's activity if the group is ready."
        }
      </p>
      <p>
        {
          'For Tier 3, the same activity works with a smaller group and more teacher modeling. The teacher can write the first sentence together with the students, then let each student finish on their own. Students who need more time can repeat the same TEKS from another lesson in the unit the next day.'
        }
      </p>
      <h2>{'Using SCR and ECR practice in intervention time'}</h2>
      <p>
        {
          'Bluebonnet builds writing every day, moving from short responses toward longer ones, with direct ECR instruction in grades 3 to 5. Intervention time is a good place to give students more turns at both.'
        }
      </p>
      <p>
        {
          'An SCR fits a single small group session. Students read a short passage, answer one question with evidence, get feedback, and revise. Because it is short, a teacher can run it with a group several times a week.'
        }
      </p>
      <p>
        {
          'An ECR takes longer, so it fits best on a Pausing Point day or across two small group sessions. On the first day students plan and draft. On the second day they read their feedback, talk with a partner and revise. Practicing in the informational, argumentative and correspondence modes students will meet on STAAR keeps the practice close to what they will see in the spring.'
        }
      </p>
      <p>
        {
          'Revising and editing activities are short and sentence-level, which makes them a good warm-up before a writing task or a quick review after one.'
        }
      </p>
      <h2>{'Three steps for your campus'}</h2>
      <p>
        <strong>{'1. Name the time and the place.'}</strong>
        {
          ' Decide with each grade level team when the practice library will be used: the daily WIN block, a set small group rotation, or Pausing Point days. Write it into the master schedule so teachers know the time is protected. When everyone uses the same block, coaching and planning get simpler.'
        }
      </p>
      <p>
        <strong>{'2. Group by TEKS, then choose by lesson.'}</strong>
        {
          " Use Curipod's reading and writing gaps dashboard or your unit assessments to group students by the standards they need. Then have teachers pick activities from the current or most recent lesson that practice those standards. Students stay with the texts they already know, and the groups can change as soon as the data does."
        }
      </p>
      <p>
        <strong>{'3. Read student writing together.'}</strong>
        {
          ' Once a week, have teachers bring a few student responses from small group to their PLC. Look at what students wrote on the first attempt and how it changed on the second. Teachers decide which groups move on, which stay, and which skills need another round. Teachers make the instructional calls, and the library supplies the activities.'
        }
      </p>
      <h2>{"About Curipod's Bluebonnet practice"}</h2>
      <p>
        {
          "Curipod is Writing Practice Students Love. It is teacher-led writing practice aligned to your district's curriculum, where students read, write and discuss together. For Bluebonnet, Curipod adds SCR and ECR practice, revising and editing, and skill practice that fit the rhythm of each unit, with feedback for every student and insight into which skills need more work. Teachers stay in charge of every lesson, and there is no extra prep."
        }
      </p>
      <p>
        {
          "Curipod's Bluebonnet practice was created independently by Curipod. It is not affiliated with or endorsed by the Texas Education Agency."
        }
      </p>
      <p>
        <Link href="/signup" passHref>
          <a>
            {
              'Sign up free to browse the full Bluebonnet practice library for grades 3–5.'
            }
          </a>
        </Link>
        {
          ' You can find every unit and lesson for your grade, with SCR, ECR, revising and editing, writing and vocabulary activities ready for small group and intervention time. To learn more about how Curipod works with Bluebonnet, visit '
        }
        <Link href="/curriculum/bluebonnet" passHref>
          <a>{'Curipod for Bluebonnet Learning'}</a>
        </Link>
        {'.'}
      </p>
      <h2>{'References'}</h2>
      <ul>
        <li>
          {'Texas Education Agency. '}
          <em>
            {
              'Specially Designed Instruction (SDI) Field User Guide for K–5 Reading Language Arts (RLA).'
            }
          </em>{' '}
          <a
            href="https://spedsupport.tea.texas.gov/sites/default/files/2025-10/sdi-field-user-guide-k-5-elar.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://spedsupport.tea.texas.gov/sites/default/files/2025-10/sdi-field-user-guide-k-5-elar.pdf'
            }
          </a>
        </li>
        <li>
          {'Texas Education Agency. '}
          <em>{'Bluebonnet Learning K–5 English Language Arts and Reading.'}</em>{' '}
          <a
            href="https://tea.texas.gov/curriculum-and-instruction/instructional-materials/bluebonnet-learning/bluebonnet-learning-k-5-english-language-arts-and-reading"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://tea.texas.gov/curriculum-and-instruction/instructional-materials/bluebonnet-learning/bluebonnet-learning-k-5-english-language-arts-and-reading'
            }
          </a>
        </li>
        <li>
          {'Texas Education Agency. '}
          <em>{'Bluebonnet Learning Guidance.'}</em>{' '}
          <a
            href="https://bluebonnet.tea.texas.gov/guidance.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            {'https://bluebonnet.tea.texas.gov/guidance.html'}
          </a>
        </li>
        <li>
          {'Curipod. '}
          <em>{'Curipod for Bluebonnet Learning.'}</em>{' '}
          <Link href="/curriculum/bluebonnet" passHref>
            <a>{'https://curipod.com/curriculum/bluebonnet'}</a>
          </Link>
        </li>
      </ul>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default BluebonnetInterventionActivitiesTier2Tier3;
